/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Tell the teacher who needs what next lesson, with the action next to the evidence.
 * @state-owner dataset store owns papers and answers; local state holds what is expanded
 * @allowed-imports React; dataset and workspace stores; shared components and hooks; module model; activities dialog
 * @forbidden direct API client; student-only surfaces
 * @public-api class detail tabs
 * @side-effects opens the assignment dialog and raises toasts
 * @tests modules/classrooms/model/next-lesson.test.ts and the workspace Playwright flows
 */

import { useState } from 'react';
import { useOverlay } from '../../../shared/components/OverlayHost';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import { AssignmentDialog } from '../../activities';
import {
  type ConceptTrend,
  nextLesson,
  nextLessonSummary,
  type Recommendation,
} from '../model/next-lesson';

export function NextLessonTab({ classIndex }: { classIndex: number }) {
  const t = useTranslator();
  const { openModal } = useOverlay();
  const classes = useDatasetStore((state) => state.classes);
  const runs = useDatasetStore((state) => state.runs);
  const answers = useDatasetStore((state) => state.answers);
  const notify = useWorkspaceUiStore((state) => state.notify);
  const [openRoster, setOpenRoster] = useState<string | null>(null);
  const [openPattern, setOpenPattern] = useState<string | null>(null);
  const [openSection, setOpenSection] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  /** What the teacher has already decided to cover, so a card stops asking twice. */
  const [planned, setPlanned] = useState<readonly string[]>([]);

  const lesson = nextLesson({ answers, classes, classIndex, runs });
  const activityId = runs.find((run) => run.classIndex === classIndex)?.activityId;

  /** The action stays in the same container as the evidence: no jump, no lost context. */
  const assign = (students: readonly string[]) => {
    if (!activityId) {
      notify(t('此班級尚無可指派的教材'));
      return;
    }
    openModal(
      <AssignmentDialog activityId={activityId} classIndex={classIndex} preselected={students} />,
    );
  };

  const plan = (label: string) => {
    setPlanned((current) => (current.includes(label) ? current : [...current, label]));
    notify(`${label} ${t('已排進下一堂課')}`);
  };

  const trustLine = [
    `${lesson.papers} ${t('次評量')}`,
    `${lesson.questions} ${t('題')}`,
    `${lesson.answers} ${t('筆有效作答')}`,
    `${lesson.comparableQuestions} ${t('題可跨卷比較')}`,
  ].join(' · ');

  const section = (id: string, title: string, dot: boolean, body: React.ReactNode) => (
    <div className={`nlsec ${openSection === id ? 'open' : ''}`} key={id}>
      <button
        className="nlsh"
        type="button"
        onClick={() => setOpenSection(openSection === id ? null : id)}
      >
        {dot ? <i className="nldot" /> : null}
        {title}
        <div className="sp" />
        <em>{openSection === id ? t('收合') : t('展開')}</em>
      </button>
      {openSection === id ? <div className="nlsb">{body}</div> : null}
    </div>
  );

  return (
    <div className="nextlesson">
      <p className="nltrust">{`${classes[classIndex].name} · ${trustLine}`}</p>

      {lesson.recommendations.length ? (
        lesson.recommendations.map((item) => (
          <RecommendationCard
            expanded={openRoster === item.concept}
            key={item.concept}
            onAssign={() => assign(item.students)}
            onPlan={() => plan(item.concept)}
            onToggleRoster={() => setOpenRoster(openRoster === item.concept ? null : item.concept)}
            planned={planned.includes(item.concept)}
            recommendation={item}
            t={t}
          />
        ))
      ) : (
        <div className="vguide">
          <b>{t('沒有跨考卷都卡住的概念')}</b>
          <span>{t('下一堂可以照原訂進度走。等這個班多考一份再回來看。')}</span>
        </div>
      )}

      <h3 className="nlh">{t('錯誤模式')}</h3>
      {lesson.patterns.length ? (
        lesson.patterns.map((pattern) => {
          const width = Math.round(
            (pattern.students.length / Math.max(1, lesson.rosterSize)) * 100,
          );
          return (
            <div className="nlpat" key={pattern.misconceptionId}>
              <button
                className="nlpb"
                type="button"
                onClick={() =>
                  setOpenPattern(
                    openPattern === pattern.misconceptionId ? null : pattern.misconceptionId,
                  )
                }
              >
                <span className="nlpl">{t(pattern.label)}</span>
                <span className="nlpt">
                  <i style={{ width: `${Math.max(6, width)}%` }} />
                </span>
                <em>{`${pattern.students.length} ${t('人')}`}</em>
              </button>
              <button className="bt sm" type="button" onClick={() => plan(pattern.label)}>
                {t('排入講解')}
              </button>
              <button className="bt sm" type="button" onClick={() => assign(pattern.students)}>
                {t('建立分組')}
              </button>
              {openPattern === pattern.misconceptionId ? (
                <div className="nlpd">
                  <span className="nlpc">{`${t('概念')}：${pattern.concept}`}</span>
                  <div className="nlnames">
                    {pattern.students.map((student) => (
                      <span className="nlname" key={student}>
                        <i>{student[0]}</i>
                        {student}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          );
        })
      ) : (
        <p className="nlempty">{t('這幾次評量沒有重複出現的錯法。')}</p>
      )}

      <div className="nlsecs">
        {section('trend', t('概念跨考卷趨勢'), false, <TrendGrid trends={lesson.trends} t={t} />)}
        {section(
          'mastered',
          `${t('已掌握的概念')} · ${lesson.mastered.length}`,
          false,
          lesson.mastered.length ? (
            <TrendGrid trends={lesson.mastered} t={t} />
          ) : (
            <p className="nlempty">{t('還沒有概念穩定在八成以上。')}</p>
          ),
        )}
        {section(
          'followup',
          t('介入後追蹤'),
          false,
          <p className="nlempty">
            {t('還沒有記錄任何介入。從上面的卡片指派之後，這裡會出現前後對照。')}
          </p>,
        )}
      </div>

      <div className="nlai">
        {summary ? (
          <pre className="nlsum">{summary}</pre>
        ) : (
          <button
            className="bt"
            type="button"
            onClick={() => setSummary(nextLessonSummary(lesson))}
          >
            {t('生成摘要')}
          </button>
        )}
      </div>
    </div>
  );
}

function RecommendationCard({
  expanded,
  onAssign,
  onPlan,
  onToggleRoster,
  planned,
  recommendation,
  t,
}: {
  expanded: boolean;
  onAssign: () => void;
  onPlan: () => void;
  onToggleRoster: () => void;
  planned: boolean;
  recommendation: Recommendation;
  t: (source: string) => string;
}) {
  const { concept, deltaPercent, papers, prerequisiteOf, questions, students, trend } =
    recommendation;
  const evidence = [
    `${t('影響')} ${students.length} ${t('人')}`,
    `${papers} ${t('次評量')}`,
    `${questions} ${t('題')}`,
    deltaPercent < 0
      ? `${t('較上次退步')} ${Math.abs(deltaPercent)}%`
      : deltaPercent > 0
        ? `${t('較上次進步')} ${deltaPercent}%`
        : t('與上次持平'),
  ].join(' · ');
  const peak = Math.max(1, ...trend.map((point) => point.percent));

  return (
    <section className="nlcard">
      <h4>{`${concept}，${students.length} ${t('人在')} ${papers} ${t('次評量都沒通過')}`}</h4>
      <p className="nlev">{evidence}</p>
      {prerequisiteOf.length ? (
        <p className="nlev">{`${prerequisiteOf.join('、')} ${t('會卡在這裡')}`}</p>
      ) : null}
      <div className="nlact">
        <div className="sp" />
        <button className="bt sm" disabled={planned} type="button" onClick={onPlan}>
          {planned ? t('已排入') : t('排入複習')}
        </button>
        <button className="bt sm pri" type="button" onClick={onAssign}>
          {`${t('指派給這')} ${students.length} ${t('人')}`}
        </button>
      </div>
      <div className="nlspark">
        {trend.map((point) => (
          <i
            key={point.runId}
            style={{ height: `${Math.max(8, Math.round((point.percent / peak) * 100))}%` }}
            title={`${point.label} ${point.percent}%`}
          />
        ))}
      </div>
      <button className="nlmore" type="button" onClick={onToggleRoster}>
        {`${t('看')} ${students.length} ${t('位學生的作答')} ${expanded ? '▴' : '▾'}`}
      </button>
      {expanded ? (
        <div className="nlnames">
          {students.map((student) => (
            <span className="nlname" key={student}>
              <i>{student[0]}</i>
              {student}
            </span>
          ))}
        </div>
      ) : null}
    </section>
  );
}

function TrendGrid({
  t,
  trends,
}: {
  t: (source: string) => string;
  trends: readonly ConceptTrend[];
}) {
  const comparable = trends.filter((trend) => trend.comparable);
  const single = trends.filter((trend) => !trend.comparable);

  return (
    <>
      <div className="nlgrid">
        {comparable.map((trend) => {
          const peak = Math.max(1, ...trend.points.map((point) => point.percent));
          return (
            <div className="nlcell" key={trend.concept}>
              <b>{trend.concept}</b>
              <div className="nlspark sm">
                {trend.points.map((point) => (
                  <i
                    key={point.runId}
                    style={{ height: `${Math.max(8, Math.round((point.percent / peak) * 100))}%` }}
                    title={`${point.label} ${point.percent}%`}
                  />
                ))}
              </div>
              <span>{`${trend.latestPercent}% · n=${trend.points[0]?.questions ?? 0}`}</span>
            </div>
          );
        })}
      </div>
      {single.length ? (
        <p className="nlempty">
          {`${t('只出現在一次評量、無法比較')}：${single.map((trend) => trend.concept).join('、')}`}
        </p>
      ) : null}
    </>
  );
}
